import classNames from 'classnames'
import { FC, SVGProps } from 'react'
import { ReactComponent as ReportIcon } from 'src/assets/svg/summary-report.svg'
import { ReactComponent as DownloadIcon } from 'src/assets/svg/Settings-download.svg'
import { useGetCustomReportsSummaryQuery } from 'src/reactQuery/useGetCustomReportsSummaryQuery'
import styles from './summarySection.module.scss'

const POSITIVE_COLOR = '#15881C'
const NEGATIVE_COLOR = '#D9532B'

// Mirrors the 2x2 grid glyph used on the Projects page, which is inline there
// rather than a shared component.
const DashboardIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="4" y="4" />
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="14" y="4" />
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="4" y="14" />
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="14" y="14" />
  </svg>
)

// The trend-up.svg asset hardcodes a green stroke, so it cannot turn red for a
// decrease. Same shape, drawn with currentColor so the sign can drive it.
const TrendGlyph: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 16 16" {...props}>
    <path
      d="M14 4L8.5 9.5L6 6L2 10"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M10 4H14V8"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

export type SummaryMetricId = 'downloads' | 'thisMonth' | 'total'

// The three metrics are fixed — only their values are data. Keeping the list
// static means the cards (and so the layout) are identical whether or not any
// figures have arrived, which is what lets the empty state say "No Data" in
// place rather than collapsing the row.
const SUMMARY_METRICS: {
  icon: FC<SVGProps<SVGSVGElement>>
  id: SummaryMetricId
  label: string
}[] = [
  { icon: ReportIcon, id: 'total', label: 'Total Reports Generated' },
  { icon: ReportIcon, id: 'thisMonth', label: 'Generated This Month' },
  { icon: DownloadIcon, id: 'downloads', label: 'Total Downloads' },
]

export interface SummaryStat {
  // Change vs the previous month. Sign drives the arrow and colour. Omitted
  // when there is no previous month to compare against.
  delta?: number
  value: number
}

export const SummarySection: FC = () => {
  const { data, isLoading } = useGetCustomReportsSummaryQuery()
  const summary = data?.data

  // Absent while loading, so the cards show "No Data" rather than a confident
  // zero for figures that have not arrived.
  const values: Partial<Record<SummaryMetricId, SummaryStat>> | undefined =
    isLoading || !summary
      ? undefined
      : {
          downloads: summary.totalDownloads,
          thisMonth: summary.generatedThisMonth,
          total: summary.totalReports,
        }

  return (
    <section className={styles.summarySection}>
      <div className={styles.sectionTitle}>
        <DashboardIcon className={styles.sectionIcon} />
        <h2 className={styles.sectionLabel}>Summary</h2>
      </div>

      <div className={styles.statsGrid}>
        {SUMMARY_METRICS.map(({ icon: Icon, id, label }) => {
          const stat = values?.[id]

          return (
            <article className={styles.statCard} key={id}>
              <div className={styles.statHeader}>
                <Icon className={styles.statIcon} />
                <span className={styles.statLabel}>{label}</span>
              </div>

              {stat ? (
                <>
                  <div className={styles.statValue}>
                    {stat.value.toLocaleString()}
                  </div>

                  {stat.delta !== undefined && (
                    <div className={styles.statTrend}>
                      <TrendGlyph
                        className={classNames(styles.trendIcon, {
                          [styles.trendIconNegative]: stat.delta < 0,
                        })}
                        style={{
                          color:
                            stat.delta < 0 ? NEGATIVE_COLOR : POSITIVE_COLOR,
                        }}
                      />
                      <span
                        className={styles.trendValue}
                        style={{
                          color:
                            stat.delta < 0 ? NEGATIVE_COLOR : POSITIVE_COLOR,
                        }}
                      >
                        {stat.delta > 0 ? `+${stat.delta}` : stat.delta}
                      </span>
                      <span className={styles.trendLabel}>vs last month</span>
                    </div>
                  )}
                </>
              ) : (
                <span className={styles.noData}>No Data</span>
              )}
            </article>
          )
        })}
      </div>
    </section>
  )
}
